import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Edit2, Trash2, Plus, Users, X, RefreshCw, CheckSquare, XSquare, Clock, Building2, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { appService, gymnastService, clubService } from '../services/supabaseService';
import { supabase } from '../config/supabase';
import { storageServiceProxy, imageServiceProxy } from '../services/proxyService';
import { calculateAge } from '../utils/formatters';
import { useNotifications } from '../components/shared/useNotifications';
import { useConfirm } from '../components/shared/ConfirmContext';
import ClubForm from '../components/shared/ClubForm';
import '../components/admin/AdminView.css';

const ClubsView = ({ 
  clubs, 
  showClubForm, 
  setShowClubForm, 
  editingClub, 
  setEditingClub, 
  deleteClub, 
  editClub,
  expandedClubs,
  toggleExpandClub,
  clubForm,
  setClubForm,
  addClub,
  cancelClubForm,
  uploadingImage,
  handleFileChange,
  clearImagePreview,
  newGymnastForm,
  setNewGymnastForm,
  addGymnastToClubForm,
  removeGymnastFromClubForm,
  editGymnastInForm,
  editingGymnast,
  setEditingGymnast,
  LEVEL_OPTIONS,
  FRANJA_OPTIONS,
  openGymnastSearch,
}) => {
  const notify = useNotifications();
  const confirm = useConfirm();
  const [searchTerm, setSearchTerm] = useState('');
  const [clubsTab, setClubsTab] = useState('registered'); // 'registered' | 'pending'
  const [currentPage, setCurrentPage] = useState(1);
  const CLUBS_PER_PAGE = 10;

  // Pending club approvals
  const [pendingClubs, setPendingClubs] = useState([]);
  const [pendingClubsLoading, setPendingClubsLoading] = useState(false);
  const [clubApprovalNotes, setClubApprovalNotes] = useState({});
  const [pendingPage, setPendingPage] = useState(1);

  const [showAthletesModal, setShowAthletesModal] = useState(false);
  const [selectedClubForAthletes, setSelectedClubForAthletes] = useState(null);
  const [clubAthletes, setClubAthletes] = useState([]);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [athleteSearch, setAthleteSearch] = useState('');
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [athleteForm, setAthleteForm] = useState({
    name: '',
    level: '',
    franja: '',
    birth_date: ''
  });
  const [athleteImageFile, setAthleteImageFile] = useState(null);
  const [athleteImagePreview, setAthleteImagePreview] = useState(null);
  const [clubsWithImages, setClubsWithImages] = useState([]);
  const [athleteImages, setAthleteImages] = useState({});

  // Cargar imágenes de los clubes cuando se carga el componente
  useEffect(() => {
    const loadClubImages = async () => {
      if (!clubs || clubs.length === 0) return;
      
      const clubsWithImageData = await Promise.all(
        clubs.map(async (club) => {
          try {
            const imageUrl = await getClubImageUrl(club.id);
            return { ...club, image_url: imageUrl };
          } catch (error) {
            console.error(`Error cargando imagen para club ${club.id}:`, error);
            return club;
          }
        })
      );
      
      setClubsWithImages(clubsWithImageData);
    };
    
    loadClubImages();
  }, [clubs]);

  // Obtener URL de imagen de un club
  const getClubImageUrl = async (clubId) => {
    try {
      // Intentar primero desde la tabla images
      const { data, error } = await supabase
        .from('images')
        .select('image_url')
        .eq('entity_type', 'club')
        .eq('entity_id', String(clubId))
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (error) {
        console.error(`Error obteniendo imagen para club ${clubId}:`, error);
      }
      
      if (data && data.length > 0 && data[0].image_url) {
        return data[0].image_url;
      }

      // Fallback: buscar directamente en el storage por nombre de archivo
      try {
        const { data: logoUrl } = await storageServiceProxy.getClubLogo(String(clubId));
        return logoUrl || null;
      } catch (storageErr) {
        console.warn(`Fallback storage search failed for club ${clubId}:`, storageErr);
      }

      return null;
    } catch (error) {
      console.error(`Error obteniendo imagen para club ${clubId}:`, error);
      return null;
    }
  };

  // Filtrar clubes por búsqueda
  const filteredClubs = useMemo(() => {
    if (!searchTerm.trim()) return clubsWithImages || [];
    return (clubsWithImages || []).filter(club =>
      club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (club.location && club.location.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [clubsWithImages, searchTerm]);

  // Pagination for registered clubs
  const totalPages = Math.ceil((filteredClubs?.length || 0) / CLUBS_PER_PAGE);
  const paginatedClubs = useMemo(() => {
    const start = (currentPage - 1) * CLUBS_PER_PAGE;
    return (filteredClubs || []).slice(start, start + CLUBS_PER_PAGE);
  }, [filteredClubs, currentPage, CLUBS_PER_PAGE]);

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // Pagination for pending clubs
  const pendingTotalPages = Math.ceil((pendingClubs?.length || 0) / CLUBS_PER_PAGE);
  const paginatedPendingClubs = useMemo(() => {
    const start = (pendingPage - 1) * CLUBS_PER_PAGE;
    return (pendingClubs || []).slice(start, start + CLUBS_PER_PAGE);
  }, [pendingClubs, pendingPage, CLUBS_PER_PAGE]);

  // Load pending club registrations
  const loadPendingClubs = useCallback(async () => {
    setPendingClubsLoading(true);
    try {
      const { data } = await clubService.getPendingRegistrations();
      setPendingClubs(data || []);
    } catch (err) {
      console.error('Error loading pending clubs:', err);
    } finally {
      setPendingClubsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingClubs();
  }, [loadPendingClubs]);

  const handleApproveClub = async (club) => {
    try {
      const notes = clubApprovalNotes[club.id] || '';
      await clubService.approveRegistration(club.id, notes);
      setPendingClubs(prev => prev.filter(c => c.id !== club.id));
      setClubApprovalNotes(prev => { const n = { ...prev }; delete n[club.id]; return n; });
      notify.success(`Club "${club.name}" aprobado exitosamente`);
      // Send welcome email
      try {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'club-approval', clubName: club.name, clubEmail: club.email, approved: true })
        });
      } catch (emailErr) { console.warn('Error enviando email de bienvenida:', emailErr); }
    } catch (err) {
      console.error('Error approving club:', err);
      notify.error('Error al aprobar el club');
    }
  };

  const handleRejectClub = async (club) => {
    const notes = clubApprovalNotes[club.id] || '';
    if (!notes.trim()) {
      notify.warning('Por favor, agrega una nota explicando el motivo del rechazo.');
      return;
    }
    try {
      await clubService.rejectRegistration(club.id, notes);
      setPendingClubs(prev => prev.filter(c => c.id !== club.id));
      setClubApprovalNotes(prev => { const n = { ...prev }; delete n[club.id]; return n; });
      notify.success(`Club "${club.name}" rechazado`);
      // Send rejection email
      try {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type: 'club-approval', clubName: club.name, clubEmail: club.email, approved: false })
        });
      } catch (emailErr) { console.warn('Error enviando email de rechazo:', emailErr); }
    } catch (err) {
      console.error('Error rejecting club:', err);
      notify.error('Error al rechazar el club');
    }
  };

  // Pagination component
  const PaginationControls = ({ current, total, onPageChange }) => {
    if (total <= 1) return null;
    const pages = [];
    for (let i = 1; i <= total; i++) pages.push(i);
    // Show limited range
    let visiblePages = pages;
    if (total > 7) {
      if (current <= 4) visiblePages = [...pages.slice(0, 5), '...', total];
      else if (current >= total - 3) visiblePages = [1, '...', ...pages.slice(total - 5)];
      else visiblePages = [1, '...', current - 1, current, current + 1, '...', total];
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 16 }}>
        <button
          onClick={() => onPageChange(Math.max(1, current - 1))}
          disabled={current === 1}
          style={{
            padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6,
            background: current === 1 ? '#f9fafb' : 'white', cursor: current === 1 ? 'default' : 'pointer',
            color: current === 1 ? '#d1d5db' : '#374151', display: 'flex', alignItems: 'center'
          }}
        >
          <ChevronLeft size={16} />
        </button>
        {visiblePages.map((p, i) => (
          p === '...' ? (
            <span key={`dots-${i}`} style={{ padding: '6px 4px', color: '#9ca3af', fontSize: '0.85rem' }}>…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              style={{
                padding: '6px 12px', border: '1px solid', borderRadius: 6,
                borderColor: p === current ? '#8b5cf6' : '#e5e7eb',
                background: p === current ? '#8b5cf6' : 'white',
                color: p === current ? 'white' : '#374151',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: p === current ? 700 : 500,
                transition: 'all 0.15s'
              }}
            >
              {p}
            </button>
          )
        ))}
        <button
          onClick={() => onPageChange(Math.min(total, current + 1))}
          disabled={current === total}
          style={{
            padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6,
            background: current === total ? '#f9fafb' : 'white', cursor: current === total ? 'default' : 'pointer',
            color: current === total ? '#d1d5db' : '#374151', display: 'flex', alignItems: 'center'
          }}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  const handleEdit = (club) => {
    setEditingClub(club);
    setShowClubForm(true);
  };

  const handleDelete = async (club) => {
    const ok = await confirm({
      title: 'Eliminar Club',
      message: `¿Estás seguro de que deseas eliminar el club ${club.name}? Esto eliminará todas sus atletas asociadas.`,
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (ok) {
      deleteClub(club.id);
    }
  };

  // Cargar atletas del club seleccionado
  const handleViewAthletes = async (club) => {
    setSelectedClubForAthletes(club);
    setShowAthletesModal(true);
    setLoadingAthletes(true);
    
    try {
      // Cargar atletas del club desde appService
      const { gymnasts, error } = await appService.loadClubGymnasts(club.id);
      if (error) throw error;
      
      setClubAthletes(gymnasts || []);
      
      // Cargar imágenes de los atletas - optimizado
      const images = {};
      
      // Obtener lista de archivos de la carpeta gymnasts una sola vez
      const { data: files } = await storageServiceProxy.list('gymnastics-images', 'gymnasts', 1000);
      
      if (files && files.length > 0 && gymnasts) {
        // Mapear archivos a atletas
        for (const gymnast of gymnasts) {
          const matchingFile = files.find(file => file.name.includes(gymnast.id));
          if (matchingFile) {
            const { data: publicUrlData } = await storageServiceProxy.getPublicUrl('gymnastics-images', `gymnasts/${matchingFile.name}`);
            if (publicUrlData && publicUrlData.publicUrl) {
              images[gymnast.id] = publicUrlData.publicUrl;
            }
          }
        }
      }
      
      setAthleteImages(images);
    } catch (error) {
      console.error('Error cargando atletas:', error);
      notify.error('Error al cargar las atletas del club');
      setClubAthletes([]);
    } finally {
      setLoadingAthletes(false);
    }
  };

  // Cerrar modal de atletas
  const closeAthletesModal = () => {
    setShowAthletesModal(false);
    setSelectedClubForAthletes(null);
    setClubAthletes([]);
    setAthleteSearch('');
    setEditingAthlete(null);
    setAthleteForm({ name: '', level: '', franja: '', birth_date: '' });
    setAthleteImageFile(null);
    setAthleteImagePreview(null);
    setAthleteImages({});
  };

  // Filtrar atletas por búsqueda
  const filteredAthletes = useMemo(() => {
    if (!athleteSearch.trim()) return clubAthletes;
    return clubAthletes.filter(athlete =>
      athlete.name.toLowerCase().includes(athleteSearch.toLowerCase())
    );
  }, [clubAthletes, athleteSearch]);

  // Agregar o editar atleta
  const handleSaveAthlete = async () => {
    if (!athleteForm.name.trim()) {
      notify.warning('Por favor ingresa el nombre de la atleta');
      return;
    }

    try {
      let imageUrl = null;

      if (editingAthlete) {
        // Actualizar atleta existente
        if (athleteImageFile) {
          imageUrl = await uploadAthleteImage(editingAthlete.id);
        }

        const updateData = {
          name: athleteForm.name,
          level: athleteForm.level,
          franja: athleteForm.franja,
          birth_date: athleteForm.birth_date || null,
          club_id: selectedClubForAthletes.id
        };

        const result = await gymnastService.update(editingAthlete.id, updateData);
        
        if (result.error) throw new Error(result.error);
        
        // Actualizar la lista local con los nuevos datos
        const updatedAthletes = clubAthletes.map(a => 
          a.id === editingAthlete.id 
            ? { 
                ...a, 
                name: athleteForm.name, 
                level: athleteForm.level, 
                franja: athleteForm.franja, 
                birth_date: athleteForm.birth_date || null,
                image_url: imageUrl || a.image_url
              }
            : a
        );
        setClubAthletes(updatedAthletes);
        notify.success('Atleta actualizada correctamente');
      } else {
        // Crear nueva atleta
        const createData = {
          name: athleteForm.name,
          level: athleteForm.level,
          franja: athleteForm.franja,
          birth_date: athleteForm.birth_date || null,
          club_id: selectedClubForAthletes.id
        };

        const result = await gymnastService.create(createData);
        
        if (result.error) throw new Error(result.error);
        
        // Subir imagen si existe
        if (athleteImageFile) {
          imageUrl = await uploadAthleteImage(result.data.id);
          result.data.image_url = imageUrl;
        }

        // Agregar la nueva atleta a la lista
        setClubAthletes([...clubAthletes, result.data]);
        notify.success('Atleta creada correctamente');
      }
      
      setEditingAthlete(null);
      setAthleteForm({ name: '', level: '', franja: '', birth_date: '' });
      setAthleteImageFile(null);
      setAthleteImagePreview(null);
    } catch (error) {
      console.error('Error guardando atleta:', error);
      notify.error('Error al guardar la atleta: ' + error.message);
    }
  };

  // Eliminar atleta
  const handleDeleteAthlete = async (athleteId) => {
    const ok = await confirm({
      title: 'Eliminar Atleta',
      message: '¿Estás seguro de que deseas eliminar esta atleta?',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (ok) {
      try {
        const result = await gymnastService.delete(athleteId);
        
        if (result.error) throw new Error(result.error);
        
        // Actualizar lista localmente
        setClubAthletes(clubAthletes.filter(a => a.id !== athleteId));
        notify.success('Atleta eliminada correctamente');
      } catch (error) {
        console.error('Error eliminando atleta:', error);
        notify.error('Error al eliminar la atleta: ' + error.message);
      }
    }
  };

  // Editar atleta
  const handleEditAthlete = (athlete) => {
    setEditingAthlete(athlete);
    setAthleteForm({
      name: athlete.name,
      level: athlete.level || '',
      franja: athlete.franja || '',
      birth_date: athlete.birth_date || ''
    });
    setAthleteImageFile(null);
    setAthleteImagePreview(athlete.image_url || null);
  };

  // Manejar cambio de archivo de imagen
  const handleAthleteImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        // Comprimir la imagen antes de guardarla
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.1, // 100KB máximo
          maxWidthOrHeight: 400, // Dimensión máxima 400px
          useWebWorker: true
        });

        setAthleteImageFile(compressedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          setAthleteImagePreview(reader.result);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Error comprimiendo imagen:', error);
        notify.error('Error al comprimir la imagen');
      }
    }
  };

  // Subir imagen de atleta
  const uploadAthleteImage = async (athleteId) => {
    if (!athleteImageFile) return null;

    try {
      // Verificar tamaño de la imagen (máximo 100KB)
      const maxSizeBytes = 100 * 1024; // 100KB
      if (athleteImageFile.size > maxSizeBytes) {
        notify.warning(`La imagen es muy grande (${(athleteImageFile.size / 1024).toFixed(2)}KB). Máximo 100KB.`);
        return null;
      }

      const fileExt = athleteImageFile.name.split('.').pop();
      const fileName = `${athleteId}-${Date.now()}.${fileExt}`;
      const filePath = `gymnasts/${fileName}`;

      console.log('📤 Subiendo imagen:', filePath, `(${(athleteImageFile.size / 1024).toFixed(2)}KB)`);

      const result = await storageServiceProxy.upload(
        'gymnastics-images',
        filePath,
        athleteImageFile
      );

      if (!result.success) {
        console.error('❌ Error upload:', result.error);
        
        // Si el bucket no existe, proporcionar instrucciones
        if (result.error?.includes('Bucket not found') || result.error?.includes('404')) {
          notify.error('⚠️ El bucket "gymnastics-images" no existe en Supabase.');
          return null;
        }
        
        throw new Error(result.error);
      }

      // Obtener URL pública de la imagen
      const publicUrl = result.data?.publicUrl;

      console.log('✅ Imagen subida:', publicUrl);
      notify.success(`Imagen guardada (${(athleteImageFile.size / 1024).toFixed(2)}KB)`);
      return publicUrl;
    } catch (error) {
      console.error('Error subiendo imagen:', error);
      notify.error(`Error al subir la imagen: ${error.message}`);
      return null;
    }
  };

  // Mostrar formulario si showClubForm es true
  if (showClubForm) {
    return (
      <ClubForm
        showClubForm={showClubForm}
        editingClub={editingClub}
        clubForm={clubForm}
        setClubForm={setClubForm}
        addClub={addClub}
        cancelClubForm={cancelClubForm}
        uploadingImage={uploadingImage}
        currentClubs={clubs}
        editClub={editClub}
        deleteClub={deleteClub}
        expandedClubs={expandedClubs}
        toggleExpandClub={toggleExpandClub}
        handleFileChange={handleFileChange}
        clearImagePreview={clearImagePreview}
        setShowClubForm={setShowClubForm}
        setEditingClub={setEditingClub}
        newGymnastForm={newGymnastForm}
        setNewGymnastForm={setNewGymnastForm}
        addGymnastToClubForm={addGymnastToClubForm}
        removeGymnastFromClubForm={removeGymnastFromClubForm}
        editGymnastInForm={editGymnastInForm}
        editingGymnast={editingGymnast}
        setEditingGymnast={setEditingGymnast}
        LEVEL_OPTIONS={LEVEL_OPTIONS}
        FRANJA_OPTIONS={FRANJA_OPTIONS}
        openGymnastSearch={openGymnastSearch}
      />
    );
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <h1>🏢 Gestión de Clubes (Global)</h1>
        
        {clubsTab === 'registered' && (
          <button 
            className="btn-primary"
            onClick={() => {
              setEditingClub(null);
              setShowClubForm(true);
            }}
          >
            <Plus size={18} />
            <span>Nuevo Club</span>
          </button>
        )}
      </div>

      {/* Sub-Tabs: Registrados / Pendientes */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 20, padding: '4px',
        background: '#f3f4f6', borderRadius: 10, width: 'fit-content'
      }}>
        <button
          onClick={() => setClubsTab('registered')}
          style={{
            padding: '8px 20px', border: 'none', borderRadius: 8,
            background: clubsTab === 'registered' ? 'white' : 'transparent',
            color: clubsTab === 'registered' ? '#111827' : '#6b7280',
            fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
            boxShadow: clubsTab === 'registered' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          <Building2 size={15} /> Clubes Registrados
          <span style={{
            background: '#e0f2fe', color: '#0369a1', padding: '2px 8px',
            borderRadius: 10, fontSize: '0.75rem', fontWeight: 700
          }}>{filteredClubs?.length || 0}</span>
        </button>
        <button
          onClick={() => setClubsTab('pending')}
          style={{
            padding: '8px 20px', border: 'none', borderRadius: 8,
            background: clubsTab === 'pending' ? 'white' : 'transparent',
            color: clubsTab === 'pending' ? '#111827' : '#6b7280',
            fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
            boxShadow: clubsTab === 'pending' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6,
            position: 'relative'
          }}
        >
          <Clock size={15} /> Pendientes de Aprobación
          {pendingClubs.length > 0 && (
            <span style={{
              background: '#06b6d4', color: 'white', padding: '2px 8px',
              borderRadius: 10, fontSize: '0.75rem', fontWeight: 700,
              animation: 'pulse 2s infinite'
            }}>{pendingClubs.length}</span>
          )}
        </button>
      </div>

      {/* ========== TAB: CLUBES REGISTRADOS ========== */}
      {clubsTab === 'registered' && (
        <>
          {/* Búsqueda */}
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Buscar por nombre o ubicación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field"
            />
          </div>

          {/* Tabla de Clubes */}
          <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Logo
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Nombre
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Ubicación
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClubs && paginatedClubs.length > 0 ? (
                    paginatedClubs.map((club, idx) => (
                      <tr 
                        key={club.id}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          background: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : '#f9fafb'}
                      >
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '8px',
                            background: '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            flexShrink: 0
                          }}>
                            {club.image_url ? (
                              <img 
                                src={club.image_url}
                                alt={club.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div style={{ fontSize: '24px', color: '#9ca3af' }}>🏢</div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', fontWeight: '500', color: '#111827' }}>
                          {club.name}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.9rem', color: '#6b7280' }}>
                          {club.location || '-'}
                        </td>
                        <td style={{ padding: '12px 16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          <button
                            onClick={() => handleViewAthletes(club)}
                            className="btn-icon"
                            title="Ver Atletas"
                          >
                            <Users size={16} />
                          </button>
                          <button
                            onClick={() => handleEdit(club)}
                            className="btn-icon"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(club)}
                            className="btn-icon danger"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                        No hay clubes registrados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <PaginationControls current={currentPage} total={totalPages} onPageChange={setCurrentPage} />

          {/* Resumen */}
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            background: '#f0f9ff',
            borderRadius: '8px',
            borderLeft: '4px solid #0284c7',
            fontSize: '0.875rem',
            color: '#0c4a6e'
          }}>
            <strong>Total de clubes registrados:</strong> {filteredClubs?.length || 0}
            {totalPages > 1 && <span> — Página {currentPage} de {totalPages}</span>}
          </div>
        </>
      )}

      {/* ========== TAB: PENDIENTES DE APROBACIÓN ========== */}
      {clubsTab === 'pending' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#71717a' }}>
                Revisa y aprueba los registros de clubes. Una vez aprobados, podrán acceder a la plataforma.
              </p>
            </div>
            <button onClick={loadPendingClubs} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#6b7280' }}>
              <RefreshCw size={14} /> Actualizar
            </button>
          </div>

          {pendingClubsLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Cargando...</div>
          ) : pendingClubs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', background: 'white', borderRadius: 12, border: '1px solid #e5e7eb' }}>
              <Building2 size={48} strokeWidth={1} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 4px' }}>Sin clubes pendientes</p>
              <p style={{ fontSize: '0.85rem' }}>No hay registros de clubes esperando aprobación en este momento.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {paginatedPendingClubs.map(club => (
                  <div key={club.id} style={{
                    border: '1.5px solid #a5f3fc', borderRadius: 14, background: 'white',
                    overflow: 'hidden', transition: 'all 0.2s'
                  }}>
                    {/* Header */}
                    <div style={{
                      background: 'linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)',
                      padding: '16px 20px', borderBottom: '1px solid #a5f3fc',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 48, height: 48, borderRadius: 10, background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
                          fontSize: '1.2rem', fontWeight: 700, border: '2px solid white'
                        }}>
                          {(club.name || 'C').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#18181b' }}>{club.name}</h4>
                          <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
                            📧 {club.email} {club.phone ? `• 📞 ${club.phone}` : ''}
                          </p>
                        </div>
                      </div>
                      <span style={{
                        padding: '5px 12px', borderRadius: 8, background: '#ecfeff',
                        color: '#155e75', fontSize: '0.78rem', fontWeight: 700,
                        border: '1px solid #a5f3fc', display: 'flex', alignItems: 'center', gap: 4
                      }}>
                        <Clock size={12} /> Pendiente
                      </span>
                    </div>

                    {/* Body */}
                    <div style={{ padding: '16px 20px' }}>
                      {/* Club details */}
                      <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: 12, marginBottom: 12
                      }}>
                        {club.location && (
                          <div style={{ padding: '10px 14px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6' }}>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ubicación</p>
                            <p style={{ margin: '4px 0 0', fontSize: '0.88rem', color: '#18181b', fontWeight: 600 }}>📍 {club.location}</p>
                          </div>
                        )}
                        {club.disciplines && club.disciplines.length > 0 && (
                          <div style={{ padding: '10px 14px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6' }}>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Disciplinas</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                              {club.disciplines.map((d, i) => (
                                <span key={i} style={{
                                  padding: '3px 10px', borderRadius: 6, background: '#e0f2fe',
                                  color: '#0369a1', fontSize: '0.78rem', fontWeight: 600
                                }}>{d}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {club.created_at && (
                          <div style={{ padding: '10px 14px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6' }}>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fecha de Registro</p>
                            <p style={{ margin: '4px 0 0', fontSize: '0.88rem', color: '#18181b', fontWeight: 600 }}>📅 {new Date(club.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                          </div>
                        )}
                      </div>

                      {/* Social media */}
                      {(club.facebook_url || club.instagram_url || club.twitter_url) && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                          {club.facebook_url && (
                            <a href={club.facebook_url} target="_blank" rel="noopener noreferrer" style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                              borderRadius: 8, background: '#eff6ff', color: '#1d4ed8', fontSize: '0.8rem',
                              fontWeight: 600, border: '1px solid #bfdbfe', textDecoration: 'none'
                            }}>Facebook</a>
                          )}
                          {club.instagram_url && (
                            <a href={club.instagram_url} target="_blank" rel="noopener noreferrer" style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                              borderRadius: 8, background: '#fdf2f8', color: '#be185d', fontSize: '0.8rem',
                              fontWeight: 600, border: '1px solid #fbcfe8', textDecoration: 'none'
                            }}>Instagram</a>
                          )}
                          {club.twitter_url && (
                            <a href={club.twitter_url} target="_blank" rel="noopener noreferrer" style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px',
                              borderRadius: 8, background: '#f8fafc', color: '#334155', fontSize: '0.8rem',
                              fontWeight: 600, border: '1px solid #e2e8f0', textDecoration: 'none'
                            }}>X / Twitter</a>
                          )}
                        </div>
                      )}

                      {/* Notes input */}
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                          <MessageSquare size={12} /> Notas (requerido para rechazar)
                        </label>
                        <textarea
                          value={clubApprovalNotes[club.id] || ''}
                          onChange={(e) => setClubApprovalNotes(prev => ({ ...prev, [club.id]: e.target.value }))}
                          placeholder="Notas sobre la aprobación o motivo del rechazo..."
                          style={{
                            width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb',
                            borderRadius: 10, fontSize: '0.85rem', resize: 'vertical',
                            minHeight: 60, fontFamily: 'inherit', boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleRejectClub(club)}
                          style={{
                            padding: '10px 20px', borderRadius: 10,
                            background: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6,
                            fontSize: '0.88rem', fontWeight: 700,
                            border: '1px solid #fecaca', transition: 'all 0.2s'
                          }}
                        >
                          <XSquare size={16} /> Rechazar
                        </button>
                        <button
                          onClick={() => handleApproveClub(club)}
                          style={{
                            padding: '10px 24px', border: 'none', borderRadius: 10,
                            background: 'linear-gradient(135deg, #06b6d4, #0891b2)', color: 'white',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            fontSize: '0.88rem', fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(6,182,212,0.3)', transition: 'all 0.2s'
                          }}
                        >
                          <CheckSquare size={16} /> Aprobar y Activar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination for pending */}
              <PaginationControls current={pendingPage} total={pendingTotalPages} onPageChange={setPendingPage} />
            </>
          )}
        </div>
      )}
      {showAthletesModal && selectedClubForAthletes && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)'
          }}>
            {/* Header Modal */}
            <div style={{
              padding: '20px',
              borderBottom: '2px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: 'white'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.3em' }}>👥 Atletas - {selectedClubForAthletes.name}</h2>
              <button
                onClick={closeAthletesModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.5em'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Contenido Modal */}
            <div style={{ padding: '20px' }}>
              {/* Búsqueda */}
              <div style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  placeholder="Buscar atleta por nombre..."
                  value={athleteSearch}
                  onChange={(e) => setAthleteSearch(e.target.value)}
                  className="input-field"
                />
              </div>

              {/* Formulario de Nueva/Editar Atleta */}
              {true && (
                <div style={{
                  background: '#f9fafb',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #e5e7eb'
                }}>
                  <h3 style={{ marginTop: 0 }}>{editingAthlete ? 'Editar Atleta' : 'Nueva Atleta'}</h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Nombre *</label>
                      <input
                        type="text"
                        value={athleteForm.name}
                        onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })}
                        placeholder="Nombre de la atleta"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Fecha de Nacimiento</label>
                      <input
                        type="date"
                        value={athleteForm.birth_date}
                        onChange={(e) => setAthleteForm({ ...athleteForm, birth_date: e.target.value })}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Nivel</label>
                      <select
                        value={athleteForm.level}
                        onChange={(e) => setAthleteForm({ ...athleteForm, level: e.target.value })}
                        className="input-field"
                      >
                        <option value="">Seleccionar nivel</option>
                        {LEVEL_OPTIONS && LEVEL_OPTIONS.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Franja</label>
                      <select
                        value={athleteForm.franja}
                        onChange={(e) => setAthleteForm({ ...athleteForm, franja: e.target.value })}
                        className="input-field"
                      >
                        <option value="">Seleccionar franja</option>
                        {FRANJA_OPTIONS && FRANJA_OPTIONS.map(franja => (
                          <option key={franja} value={franja}>{franja}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Sección de Foto */}
                  <div style={{ marginBottom: '12px', padding: '12px', background: '#ffffff', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Foto de la Atleta (opcional)</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <label style={{
                        padding: '8px 12px',
                        background: '#8b5cf6',
                        color: 'white',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '500'
                      }}>
                        📷 Seleccionar Foto
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAthleteImageChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                      {athleteImagePreview && (
                        <button
                          onClick={() => {
                            setAthleteImageFile(null);
                            setAthleteImagePreview(null);
                          }}
                          style={{
                            padding: '8px 12px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          ✕ Limpiar
                        </button>
                      )}
                    </div>
                    {athleteImagePreview && (
                      <div style={{ marginTop: '8px' }}>
                        <img
                          src={athleteImagePreview}
                          alt="Preview"
                          style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                            border: '2px solid #8b5cf6'
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={handleSaveAthlete}
                      className="btn-primary"
                      style={{ flex: 1 }}
                    >
                      {editingAthlete ? 'Actualizar' : 'Agregar'}
                    </button>
                    {editingAthlete && (
                      <button
                        onClick={() => {
                          setEditingAthlete(null);
                          setAthleteForm({ name: '', level: '', franja: '', birth_date: '' });
                          setAthleteImageFile(null);
                          setAthleteImagePreview(null);
                        }}
                        style={{
                          padding: '10px 16px',
                          background: '#f3f4f6',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Tabla de Atletas */}
              {loadingAthletes ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                  Cargando atletas...
                </div>
              ) : filteredAthletes.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600' }}>Avatar</th>
                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600' }}>Nombre</th>
                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600' }}>Edad</th>
                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600' }}>Nivel</th>
                        <th style={{ padding: '10px', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600' }}>Franja</th>
                        <th style={{ padding: '10px', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600' }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAthletes.map((athlete, idx) => (
                        <tr
                          key={athlete.id}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            background: idx % 2 === 0 ? '#ffffff' : '#f9fafb'
                          }}
                        >
                          <td style={{ padding: '10px' }}>
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background: '#e5e7eb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              flexShrink: 0
                            }}>
                              {athleteImages[athlete.id] ? (
                                <img
                                  src={athleteImages[athlete.id]}
                                  alt={athlete.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <span style={{ fontSize: '20px' }}>👤</span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '10px', fontSize: '0.9rem' }}>{athlete.name}</td>
                          <td style={{ padding: '10px', fontSize: '0.9rem', color: '#6b7280' }}>{calculateAge(athlete.birth_date) ?? athlete.age ?? '—'}</td>
                          <td style={{ padding: '10px', fontSize: '0.9rem', color: '#6b7280' }}>{athlete.level || '-'}</td>
                          <td style={{ padding: '10px', fontSize: '0.9rem', color: '#6b7280' }}>{athlete.franja || '-'}</td>
                          <td style={{ padding: '10px', display: 'flex', justifyContent: 'center', gap: '6px' }}>
                            <button
                              onClick={() => handleEditAthlete(athlete)}
                              className="btn-icon"
                              title="Editar"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteAthlete(athlete.id)}
                              className="btn-icon danger"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                  No hay atletas registradas para este club
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubsView;
